const trimTrailingZeros = (value: number, maximumFractionDigits: number) =>
  value
    .toFixed(maximumFractionDigits)
    .replace(/(\.\d*?[1-9])0+$/, "$1")
    .replace(/\.0+$/, "");

const appendConvertedUnit = (
  input: string,
  pattern: RegExp,
  convert: (value: number) => number,
  convertedUnit: string,
  maximumFractionDigits: number,
  shouldSkipMatch?: (match: string, offset: number, source: string) => boolean,
) =>
  input.replace(
    pattern,
    (match, numericValue: string, ...rest: Array<string | number>) => {
      const offset = rest[rest.length - 2];
      const source = rest[rest.length - 1];

      if (
        typeof offset !== "number" ||
        typeof source !== "string" ||
        shouldSkipMatch?.(match, offset, source)
      ) {
        return match;
      }

      const parsedValue = Number(numericValue);
      if (!Number.isFinite(parsedValue)) {
        return match;
      }

      const convertedValue = trimTrailingZeros(
        convert(parsedValue),
        maximumFractionDigits,
      );

      return `${match} (${convertedValue} ${convertedUnit})`;
    },
  );

export const formatProductOptionValue = (value: string): string => {
  if (!value) {
    return value;
  }

  let formattedValue = value;

  // Fluid ounces must be handled before plain ounces so volumes get ml, not g.
  formattedValue = appendConvertedUnit(
    formattedValue,
    /(\d+(?:\.\d+)?)\s*(fl\.?\s*oz|fluid ounces?|fluid ounce)\b(?!\s*\([^)]*\bml\b[^)]*\))/gi,
    (amount) => amount * 29.5735,
    "ml",
    1,
  );

  formattedValue = appendConvertedUnit(
    formattedValue,
    /(\d+(?:\.\d+)?)\s*(oz|ounces?|ounce)\b(?!\s*\([^)]*\bg\b[^)]*\))/gi,
    (amount) => amount * 28.3495,
    "g",
    1,
    (_match, offset, source) => {
      const prefix = source
        .slice(Math.max(0, offset - 8), offset)
        .toLowerCase();
      return /(?:fl\.?\s*|fluid\s+)$/.test(prefix);
    },
  );

  formattedValue = appendConvertedUnit(
    formattedValue,
    /(\d+(?:\.\d+)?)\s*(inches|inch|in\.)\b(?!\s*\([^)]*\bcm\b[^)]*\))/gi,
    (amount) => amount * 2.54,
    "cm",
    2,
  );

  formattedValue = appendConvertedUnit(
    formattedValue,
    /(\d+(?:\.\d+)?)\s*(feet|foot|ft\.?)\b(?!\s*\([^)]*\bm\b[^)]*\))/gi,
    (amount) => amount * 0.3048,
    "m",
    2,
  );

  return formattedValue;
};
